import { getToken, clearToken } from '../utils/authToken';
import { useState } from 'react';
import { API_URL, API } from '../config';
import { AlertTriangle, CheckCircle, Info, Wrench, ChevronDown, ChevronUp, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


const CodeAnalysisResults = ({ analysis, codeBlocks, onApplyFix }) => {
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFixing, setIsFixing] = useState(false);
  const [fixingIndex, setFixingIndex] = useState(null);
  
  const isSpanish = language === 'es';
  
  if (!analysis || !analysis.analyzed) return null;
  
  const { has_errors, quality_score, errors, summary, total_errors } = analysis;
  
  // Determinar el color del score
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 50) return 'text-orange-400';
    return 'text-red-400';
  };
  
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-400" />;
    }
  };
  
  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    const labels = {
      critical: isSpanish ? 'Crítico' : 'Critical',
      warning: isSpanish ? 'Advertencia' : 'Warning',
      info: 'Info'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded border ${colors[severity] || colors.info}`}>
        {labels[severity] || severity}
      </span>
    );
  };
  
  const handleFixError = async (error, index) => {
    if (!codeBlocks || codeBlocks.length === 0) return;
    
    setIsFixing(true);
    setFixingIndex(index);
    
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      
      // Encontrar el bloque de código correspondiente
      const targetBlock = codeBlocks.find(b => 
        b.language === error.source_language || 
        b.code.includes(error.code_snippet)
      ) || codeBlocks[0];
      
      const response = await axios.post(`${API}/code/fix`, {
        code: targetBlock.code,
        errors: [error],
        language: targetBlock.language || 'javascript',
        user_language: language
      }, { headers });
      
      if (response.data.success && onApplyFix) {
        onApplyFix(response.data.fixed_code, targetBlock.language);
        toast.success(isSpanish ? '¡Código corregido!' : 'Code fixed!');
      } else {
        toast.info(isSpanish ? 'No se pudo aplicar la corrección automática' : 'Could not apply automatic fix');
      }
    } catch (err) {
      console.error('Fix error:', err);
      toast.error(isSpanish ? 'Error al corregir' : 'Error fixing code');
    } finally {
      setIsFixing(false);
      setFixingIndex(null);
    }
  };
  
  const handleFixAll = async () => {
    if (!codeBlocks || codeBlocks.length === 0 || !errors || errors.length === 0) return;
    
    setIsFixing(true);
    
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      
      // Corregir el primer bloque con todos los errores
      const targetBlock = codeBlocks[0];
      
      const response = await axios.post(`${API}/code/fix`, {
        code: targetBlock.code,
        errors: errors,
        language: targetBlock.language || 'javascript',
        user_language: language
      }, { headers });
      
      if (response.data.success && onApplyFix) {
        onApplyFix(response.data.fixed_code, targetBlock.language);
        toast.success(
          isSpanish 
            ? `¡${response.data.changes?.length || errors.length} corrección(es) aplicada(s)!` 
            : `${response.data.changes?.length || errors.length} fix(es) applied!`
        );
      }
    } catch (err) {
      console.error('Fix all error:', err);
      toast.error(isSpanish ? 'Error al corregir' : 'Error fixing code');
    } finally {
      setIsFixing(false);
    }
  };
  
  return (
    <div className="mt-4 rounded-xl border border-zinc-700/50 bg-zinc-800/30 overflow-hidden" data-testid="code-analysis-results">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-700/30 transition-colors"
        data-testid="analysis-toggle"
      >
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-indigo-400" />
          <span className="text-sm font-medium text-zinc-200">
            {isSpanish ? 'Análisis de Código' : 'Code Analysis'}
          </span>
          {has_errors ? (
            <span className="flex items-center gap-1 text-xs text-yellow-400">
              <AlertTriangle className="w-3 h-3" />
              {total_errors} {isSpanish ? 'problema(s)' : 'issue(s)'}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-green-400">
              <CheckCircle className="w-3 h-3" />
              {isSpanish ? 'Sin errores' : 'No errors'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-mono ${getScoreColor(quality_score)}`}>
            {quality_score}/100
          </span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-zinc-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          )}
        </div>
      </button>
      
      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Summary */}
          {summary && (
            <p className="text-sm text-zinc-400">{summary}</p>
          )}
          
          {/* Errors List */}
          {errors && errors.length > 0 && (
            <div className="space-y-2">
              {errors.map((error, index) => (
                <div 
                  key={index}
                  className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-700/50"
                  data-testid={`error-item-${index}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      {getSeverityIcon(error.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getSeverityBadge(error.severity)}
                          {error.line && (
                            <span className="text-xs text-zinc-500">
                              {isSpanish ? 'Línea' : 'Line'} {error.line}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-200 mt-1">{error.message}</p>
                        {error.explanation && (
                          <p className="text-xs text-zinc-400 mt-1">{error.explanation}</p>
                        )}
                        {error.code_snippet && (
                          <code className="block text-xs font-mono text-red-300 mt-2 p-2 bg-zinc-950 rounded border border-zinc-800 overflow-x-auto">
                            {error.code_snippet}
                          </code>
                        )}
                        {error.fix && (
                          <div className="mt-2">
                            <span className="text-xs text-green-400">{isSpanish ? 'Sugerencia:' : 'Suggestion:'}</span>
                            <code className="block text-xs font-mono text-green-300 mt-1 p-2 bg-zinc-950 rounded border border-zinc-800 overflow-x-auto">
                              {error.fix}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                    {error.fix && onApplyFix && (
                      <button
                        onClick={() => handleFixError(error, index)}
                        disabled={isFixing}
                        className="flex-shrink-0 p-1.5 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-400 transition-colors disabled:opacity-50"
                        title={isSpanish ? 'Aplicar corrección' : 'Apply fix'}
                        data-testid={`fix-btn-${index}`}
                      >
                        {isFixing && fixingIndex === index ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Wrench className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Fix All Button */}
          {has_errors && errors && errors.length > 1 && onApplyFix && (
            <button
              onClick={handleFixAll}
              disabled={isFixing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="fix-all-btn"
            >
              {isFixing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wrench className="w-4 h-4" />
              )}
              {isSpanish ? 'Corregir Todos' : 'Fix All'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeAnalysisResults;
